import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";
import { NotificationDot } from "./notification-dot";

/**
 * Плотная строчная кнопка: 24px, r6, паддинги 2/8/2/6, зазор 6 (DS ButtonSm,
 * figma 1:86). `shape="chip"` — r4 и обычный регистр, для действий внутри текста.
 *
 * Вторичная ButtonSm та же пара, что и Button: на белом `#f1f2f4`, на сером
 * холодная `bg-btn-on-grey`. Ховер только затемняет заливку.
 */
// `not-disabled:hover:`, а НЕ `enabled:hover:`: псевдокласс `:enabled`
// существует только у form-элементов, и те же классы, надетые на `<a>`
// («Написать в поддержку», «Открыть сервис»), оставались вовсе без ховера.
// `:not(:disabled)` истинно и для ссылки, и для включённой кнопки.
const buttonSmVariants = cva(
	[
		// `min-h-6`, а не `h-6`: 24px — высота ОДНОЙ строки, и при фиксированной
		// высоте длинная подпись просто вылезала из плашки. Однострочная кнопка
		// остаётся ровно 24px, вторая строка появляется только там, где нужна.
		"t-btn tap-target relative inline-flex min-h-6 items-center justify-center gap-1.5",
		"text-center text-fg break-words hyphens-auto",
		"iec-press-sm",
		"cursor-pointer focus-ring",
		"disabled:cursor-not-allowed disabled:bg-btn-disabled disabled:text-fg-disabled",
	],
	{
		variants: {
			variant: {
				primary: "bg-brand not-disabled:hover:bg-brand-hover",
				second: "",
				ghost: "bg-transparent not-disabled:hover:bg-black/4",
			},
			shape: {
				button: "rounded-sm px-2 py-0.5 pl-1.5",
				chip: "rounded-chip px-2 py-0.5 normal-case",
			},
			onGrey: { true: "", false: "" },
		},
		compoundVariants: [
			{
				variant: "second",
				onGrey: false,
				class: "bg-btn-secondary not-disabled:hover:bg-btn-secondary-hover",
			},
			{
				variant: "second",
				onGrey: true,
				class: "bg-btn-on-grey not-disabled:hover:bg-btn-on-grey-hover",
			},
		],
		defaultVariants: { variant: "second", shape: "button", onGrey: false },
	},
);

export interface ButtonSmProps
	extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled">,
		VariantProps<typeof buttonSmVariants> {
	state?: "default" | "hover" | "disabled";
	/**
	 * Нативный проп рядом с `state="disabled"`: `state` — режим показа для
	 * витрины компонентов, а `disabled` — обычное «кнопка сейчас недоступна»,
	 * которое приходит из формы. Раньше он был вырезан из типа, и вызывающая
	 * сторона писала `state={busy ? "disabled" : "default"}` — состояние формы
	 * через проп внешнего вида.
	 */
	disabled?: boolean;
	iconLeft?: ReactNode;
	iconRight?: ReactNode;
	/** Счётчик в правом верхнем углу. `0` показывается, `undefined` — нет. */
	notification?: number | string;
	children?: ReactNode;
}

export function ButtonSm({
	variant,
	shape,
	onGrey,
	state = "default",
	disabled: disabledProp,
	iconLeft,
	iconRight,
	notification,
	className,
	children,
	type = "button",
	...rest
}: ButtonSmProps) {
	const disabled = state === "disabled" || disabledProp === true;
	const forcedHover = state === "hover";

	return (
		<button
			type={type}
			disabled={disabled}
			className={cn(
				buttonSmVariants({ variant, shape, onGrey }),
				forcedHover && variant === "primary" && "bg-brand-hover",
				forcedHover &&
					variant !== "primary" &&
					variant !== "ghost" &&
					(onGrey ? "bg-btn-on-grey-hover" : "bg-btn-secondary-hover"),
				forcedHover && variant === "ghost" && "bg-black/4",
				className,
			)}
			{...rest}
		>
			{iconLeft}
			<span>{children}</span>
			{iconRight}
			{notification != null ? (
				<NotificationDot value={notification} className="-top-[5px] -right-[5px] absolute" />
			) : null}
		</button>
	);
}

export { buttonSmVariants };
